import { Button, Descriptions, Drawer, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, LinkOutlined } from '@ant-design/icons';
import { MissionReviewRow } from 'pages/star-shared/types';
import { formatDateTime, personName, reviewCapReached } from 'pages/star-shared/utils';
import StarAmount from 'pages/star-shared/components/star-amount';
import { ProgressStatusTag } from 'pages/star-shared/components/status-tags';
import { colors } from 'theme/tokens';

export type ReviewAction = 'approve' | 'reject';

interface Props {
  review: MissionReviewRow | null;
  onClose: () => void;
  onAction: (review: MissionReviewRow, action: ReviewAction) => void;
}

const ReviewDrawer = ({ review, onClose, onAction }: Props) => {
  const pending = !review?.status || review.status === 'PENDING_REVIEW';
  return (
    <Drawer title="Chi tiết báo cáo nhiệm vụ" open={!!review} onClose={onClose} width={560} destroyOnHidden>
      {review && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ID">#{review.id}</Descriptions.Item>
            <Descriptions.Item label="Người nộp">
              {personName(review.user)}
              {review.user?.username ? ` (${review.user.username})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Nhiệm vụ">{review.mission?.title || '—'}</Descriptions.Item>
            <Descriptions.Item label="Thưởng khi duyệt">
              <StarAmount value={review.mission?.rewardStars} />
            </Descriptions.Item>
            <Descriptions.Item label="Kỳ">{review.periodKey}</Descriptions.Item>
            {/* Doc 10 §6.2 / §7.1 — 4 dòng giới hạn, ẩn khi máy chủ cũ không trả. */}
            {review.attempt !== undefined && (
              <Descriptions.Item label="Lần nộp">
                {review.maxRewardsPerPeriod !== null && review.maxRewardsPerPeriod !== undefined
                  ? `${review.attempt}/${review.maxRewardsPerPeriod} lượt thưởng của kỳ`
                  : `Lần ${review.attempt}`}
              </Descriptions.Item>
            )}
            {review.rejectCount !== undefined && (
              <Descriptions.Item label="Bị từ chối">
                {review.rejectCount} lần trong kỳ này
              </Descriptions.Item>
            )}
            {review.submissionsToday !== undefined && (
              <Descriptions.Item label="Cùng ngày nộp">
                {review.submissionsToday} báo cáo (mọi nhiệm vụ, tính theo ngày VN của lần nộp)
              </Descriptions.Item>
            )}
            {review.monthPaid !== undefined && (
              <Descriptions.Item label="Sao nộp tay tháng này">
                {review.monthCap
                  ? `${review.monthPaid}/${review.monthCap}`
                  : `${review.monthPaid} (không trần)`}
                {review.countsToCap === false && ' · nhiệm vụ Một lần: ngoài trần'}
                {pending && reviewCapReached(review) && (
                  <div style={{ color: colors.warningInk, fontWeight: 600 }}>
                    Đã chạm trần tháng — duyệt sẽ không cộng Sao.
                  </div>
                )}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Trạng thái">
              <ProgressStatusTag value={review.status || 'PENDING_REVIEW'} />
            </Descriptions.Item>
            <Descriptions.Item label="Nộp lúc">{formatDateTime(review.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Minh chứng">
              {review.evidenceUrl ? (
                <a href={review.evidenceUrl} target="_blank" rel="noreferrer">
                  <LinkOutlined /> {review.evidenceUrl}
                </a>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Ghi chú của người nộp">
              <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {review.evidenceNote || '—'}
              </Typography.Paragraph>
            </Descriptions.Item>
            {review.reviewNote && (
              <Descriptions.Item label="Ghi chú duyệt">{review.reviewNote}</Descriptions.Item>
            )}
            {review.reviewedAt && (
              <Descriptions.Item label="Duyệt lúc">{formatDateTime(review.reviewedAt)}</Descriptions.Item>
            )}
          </Descriptions>
          {pending && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button danger icon={<CloseOutlined />} onClick={() => onAction(review, 'reject')}>
                Từ chối
              </Button>
              <Button type="primary" icon={<CheckOutlined />} onClick={() => onAction(review, 'approve')}>
                Duyệt
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ReviewDrawer;
